// PitchEditorChart.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend
);

const isFiniteNumber = (value) => Number.isFinite(Number(value));

function nearestPitchY(xsAbs, ys, cursorAbs) {
  if (
    cursorAbs == null ||
    !Array.isArray(xsAbs) ||
    !Array.isArray(ys) ||
    xsAbs.length === 0 ||
    ys.length === 0
  ) {
    return null;
  }

  let low = 0;
  let high = xsAbs.length - 1;

  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const x = Number(xsAbs[middle]);

    if (x < cursorAbs) {
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  const rightIndex = Math.min(low, xsAbs.length - 1);
  const leftIndex = Math.max(0, rightIndex - 1);

  const candidates = [leftIndex, rightIndex]
    .map((index) => ({
      index,
      distance: Math.abs(Number(xsAbs[index]) - cursorAbs),
    }))
    .sort((a, b) => a.distance - b.distance);

  for (const candidate of candidates) {
    const y = Number(ys[candidate.index]);

    // The backend uses 0.001 as the unvoiced baseline.
    if (Number.isFinite(y) && y > 0.01) {
      return y;
    }
  }

  return null;
}

export default function PitchEditorChart({
  pitch,
  intensity,
  segment,
  audioRef,
  onSetWordBoundaryAbs,
  onSetLastWordEndAbs,
  onSetSegmentEndAbs,
  onPlayRangeAbs,
}) {
  const chartRef = useRef(null);
  const overlayRef = useRef(null);

  const [drag, setDrag] = useState(null);
  const [cursorAbs, setCursorAbs] = useState(null);
  const [wordButtonLayout, setWordButtonLayout] = useState([]);

  const words = segment?.words ?? [];
  const segStart = Number(segment?.start ?? 0);
  const segEnd = Number(segment?.end ?? segStart);

  const lastWordEndAbs = useMemo(() => {
    if (!words.length) {
      return segEnd;
    }

    const end = Number(words[words.length - 1]?.end);
    return Number.isFinite(end) ? end : segEnd;
  }, [words, segEnd]);

  const xMin = segStart;
  const xMax = Math.max(segEnd, lastWordEndAbs) + 1e-6;

  /*
   * /analysis/edit_narrator always returns pitch.xs relative to the selected
   * segment WAV. The chart itself uses absolute source-file times.
   */
  const xMode = "relative";

  const { xsAbs, ysClean, yMin, yMax } = useMemo(() => {
    const rawXs = pitch?.xs ?? [];
    const rawYs = pitch?.ys ?? [];

    const points = [];

    for (let index = 0; index < rawXs.length; index += 1) {
      const relativeX = Number(rawXs[index]);

      if (!Number.isFinite(relativeX)) {
        continue;
      }

      const absoluteX = segStart + relativeX;

      if (absoluteX < xMin || absoluteX > xMax) {
        continue;
      }

      const rawY = Number(rawYs[index]);

      const y =
        Number.isFinite(rawY) && rawY > 0
          ? rawY
          : null;

      points.push({
        x: absoluteX,
        y,
      });
    }

    const validPitchValues = points
      .map((point) => point.y)
      .filter((value) => Number.isFinite(value) && value > 0.01);

    const minimum = validPitchValues.length
      ? Math.min(...validPitchValues)
      : 0;

    const maximum = validPitchValues.length
      ? Math.max(...validPitchValues)
      : 1;

    return {
      xsAbs: points.map((point) => point.x),
      ysClean: points.map((point) => point.y),
      yMin: minimum,
      yMax: maximum,
    };
  }, [pitch, segStart, xMin, xMax]);

  const yRange = Math.max(1e-6, yMax - yMin);

  /*
   * Intensity is continuous through voiced and unvoiced regions, so it is
   * useful for placing segment/word boundaries. Keep it on a separate dB
   * scale so its numeric range does not compress the pitch curve.
   */
  const {
    intensityPoints,
    intensityMin,
    intensityMax,
    hasIntensity,
  } = useMemo(() => {
    const rawXs = intensity?.xs ?? [];
    const rawYs = intensity?.ys ?? [];
    const points = [];

    for (let index = 0; index < rawXs.length; index += 1) {
      const relativeX = Number(rawXs[index]);
      const value = Number(rawYs[index]);

      if (!Number.isFinite(relativeX) || !Number.isFinite(value)) {
        continue;
      }

      const absoluteX = segStart + relativeX;

      if (absoluteX < xMin || absoluteX > xMax) {
        continue;
      }

      points.push({
        x: absoluteX,
        y: value,
      });
    }

    const values = points.map((point) => point.y);
    const minimum = values.length ? Math.min(...values) : 0;
    const maximum = values.length ? Math.max(...values) : 1;

    return {
      intensityPoints: points,
      intensityMin: minimum,
      intensityMax: maximum,
      hasIntensity: points.length > 0,
    };
  }, [intensity, segStart, xMin, xMax]);

  const intensityRange = Math.max(1e-6, intensityMax - intensityMin);

  /*
   * Read the actual audio element continuously.
   *
   * Passing audio.currentTime as an ordinary prop gives only a snapshot because
   * changes to a ref do not trigger React renders.
   */
  useEffect(() => {
    let animationFrameId = null;
    let previousRenderedTime = null;

    const tick = () => {
      const audio = audioRef?.current;
      const currentTime = Number(audio?.currentTime);

      const nextCursor =
        Number.isFinite(currentTime) &&
        currentTime >= xMin &&
        currentTime <= xMax
          ? currentTime
          : null;

      if (nextCursor == null) {
        if (previousRenderedTime != null) {
          previousRenderedTime = null;
          setCursorAbs(null);
        }
      } else if (
        previousRenderedTime == null ||
        Math.abs(nextCursor - previousRenderedTime) >= 0.005
      ) {
        previousRenderedTime = nextCursor;
        setCursorAbs(nextCursor);
      }

      animationFrameId = requestAnimationFrame(tick);
    };

    animationFrameId = requestAnimationFrame(tick);

    return () => {
      if (animationFrameId != null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [audioRef, xMin, xMax]);

  useEffect(() => {
    setCursorAbs(null);
  }, [segment?.id, segStart, segEnd]);

  const internalBoundariesAbs = useMemo(() => {
    const boundaries = [];

    for (let index = 0; index < words.length - 1; index += 1) {
      const time = Number(words[index]?.end);

      if (Number.isFinite(time)) {
        boundaries.push({
          index,
          x: time,
        });
      }
    }

    return boundaries;
  }, [words]);

  const lastBoundaryAbs = useMemo(() => {
    if (!words.length) {
      return null;
    }

    const time = Number(words[words.length - 1]?.end);

    if (!Number.isFinite(time)) {
      return null;
    }

    return { x: time };
  }, [words]);

  /*
   * Position the word buttons from the live Chart.js x-scale rather than from
   * simple percentages. This keeps them aligned with the plot area even when
   * the left/right axes change width. Whenever a word boundary moves, the
   * segment.words values change and the buttons are recalculated.
   */
  useEffect(() => {
    let animationFrameId = null;
    let resizeObserver = null;

    const updateLayout = () => {
      const chart = chartRef.current;
      const xScale = chart?.scales?.x;
      const chartArea = chart?.chartArea;

      if (!chart || !xScale || !chartArea) {
        setWordButtonLayout([]);
        return;
      }

      const nextLayout = words
        .map((word, index) => {
          const start = Number(word?.start);
          const end = Number(word?.end);

          if (
            !Number.isFinite(start) ||
            !Number.isFinite(end) ||
            end <= start
          ) {
            return null;
          }

          const rawLeft = xScale.getPixelForValue(start);
          const rawRight = xScale.getPixelForValue(end);

          const left = Math.max(chartArea.left, Math.min(chartArea.right, rawLeft));
          const right = Math.max(chartArea.left, Math.min(chartArea.right, rawRight));
          const width = Math.max(0, right - left);

          if (width <= 0) {
            return null;
          }

          return {
            index,
            text: String(word?.text ?? "").trim() || `Word ${index + 1}`,
            start,
            end,
            left,
            width,
          };
        })
        .filter(Boolean);

      setWordButtonLayout(nextLayout);
    };

    animationFrameId = requestAnimationFrame(updateLayout);

    const chartContainer = chartRef.current?.canvas?.parentElement;

    if (
      chartContainer &&
      typeof ResizeObserver !== "undefined"
    ) {
      resizeObserver = new ResizeObserver(() => {
        if (animationFrameId != null) {
          cancelAnimationFrame(animationFrameId);
        }

        animationFrameId = requestAnimationFrame(updateLayout);
      });

      resizeObserver.observe(chartContainer);
    }

    return () => {
      if (animationFrameId != null) {
        cancelAnimationFrame(animationFrameId);
      }

      resizeObserver?.disconnect();
    };
  }, [
    words,
    xMin,
    xMax,
    hasIntensity,
    intensityMin,
    intensityMax,
  ]);

  const cursorPitchY = useMemo(
    () => nearestPitchY(xsAbs, ysClean, cursorAbs),
    [xsAbs, ysClean, cursorAbs]
  );

  const data = useMemo(() => {
    const datasets = [];
    const extend = yRange * 0.15;

    if (hasIntensity) {
      datasets.push({
        label: "Intensity",
        data: intensityPoints,
        yAxisID: "yIntensity",
        borderColor: "rgba(100,116,139,0.38)",
        borderWidth: 1.5,
        tension: 0.2,
        pointRadius: 0,
        spanGaps: true,
        showLine: true,
        order: 2,
      });
    }

    datasets.push({
      label: "Pitch",
      data: xsAbs.map((x, index) => ({
        x,
        y: ysClean[index],
      })),
      yAxisID: "y",
      borderColor: "#2563eb",
      borderWidth: 2,
      tension: 0.25,
      pointRadius: 0,
      spanGaps: false,
      showLine: true,
    });

    datasets.push({
      label: "SegStart",
      data: [
        { x: segStart, y: yMin - extend },
        { x: segStart, y: yMax + extend },
      ],
      borderColor: "rgba(34,197,94,0.95)",
      borderWidth: 2,
      pointRadius: 0,
      showLine: true,
    });

    datasets.push({
      label: "SegEnd",
      data: [
        { x: segEnd, y: yMin - extend },
        { x: segEnd, y: yMax + extend },
      ],
      borderColor: "rgba(239,68,68,0.95)",
      borderWidth: 2,
      pointRadius: 0,
      showLine: true,
    });

    for (const boundary of internalBoundariesAbs) {
      datasets.push({
        label: `WB-${boundary.index}`,
        data: [
          { x: boundary.x, y: yMin - extend },
          { x: boundary.x, y: yMax + extend },
        ],
        borderColor: "rgba(107,114,128,0.55)",
        borderWidth: 1,
        borderDash: [5, 5],
        pointRadius: 0,
        showLine: true,
      });
    }

    if (lastBoundaryAbs) {
      datasets.push({
        label: "WB-LAST",
        data: [
          { x: lastBoundaryAbs.x, y: yMin - extend },
          { x: lastBoundaryAbs.x, y: yMax + extend },
        ],
        borderColor: "rgba(107,114,128,0.7)",
        borderWidth: 1.5,
        borderDash: [5, 5],
        pointRadius: 0,
        showLine: true,
      });
    }

    if (cursorAbs != null) {
      datasets.push({
        label: "Cursor",
        data: [
          { x: cursorAbs, y: yMin - extend },
          { x: cursorAbs, y: yMax + extend },
        ],
        borderColor: "rgba(17,24,39,0.95)",
        borderWidth: 2,
        pointRadius: 0,
        showLine: true,
      });
    }

    if (cursorAbs != null && cursorPitchY != null) {
      datasets.push({
        label: "CursorBall",
        data: [
          {
            x: cursorAbs,
            y: cursorPitchY,
          },
        ],
        borderColor: "rgba(17,24,39,1)",
        backgroundColor: "rgba(17,24,39,1)",
        pointRadius: 5,
        pointHoverRadius: 5,
        showLine: false,
      });
    }

    return { datasets };
  }, [
    xsAbs,
    ysClean,
    intensityPoints,
    hasIntensity,
    segStart,
    segEnd,
    internalBoundariesAbs,
    lastBoundaryAbs,
    cursorAbs,
    cursorPitchY,
    yMin,
    yMax,
    yRange,
  ]);

  const options = useMemo(() => {
    const extend = yRange * 0.15;
    const intensityExtend = Math.max(1, intensityRange * 0.08);

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      parsing: false,
      normalized: true,
      scales: {
        x: {
          type: "linear",
          min: xMin,
          max: xMax,
          grid: {
            display: false,
          },
          ticks: {
            display: true,
          },
          title: {
            display: true,
            text: "Time (s) — absolute",
          },
        },
        y: {
          min: yMin - extend,
          max: yMax + extend,
          grid: {
            color: "rgba(0,0,0,0.05)",
          },
          title: {
            display: true,
            text: "Pitch (Hz)",
          },
        },
        yIntensity: {
          type: "linear",
          position: "right",
          display: hasIntensity,
          min: intensityMin - intensityExtend,
          max: intensityMax + intensityExtend,
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            color: "rgba(100,116,139,0.7)",
            maxTicksLimit: 5,
          },
          title: {
            display: hasIntensity,
            text: "Intensity (dB)",
            color: "rgba(100,116,139,0.8)",
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
        datalabels: {
          display: false,
        },
      },
      interaction: {
        mode: "nearest",
        axis: "x",
        intersect: false,
      },
    };
  }, [
    xMin,
    xMax,
    yMin,
    yMax,
    yRange,
    intensityMin,
    intensityMax,
    intensityRange,
    hasIntensity,
  ]);

  const pointerToXAbs = (event) => {
    const chart = chartRef.current;

    if (!chart) {
      return null;
    }

    const xScale = chart.scales?.x;

    if (!xScale) {
      return null;
    }

    const rectangle = chart.canvas.getBoundingClientRect();
    const pixelX = event.clientX - rectangle.left;
    const value = xScale.getValueForPixel(pixelX);

    return Number.isFinite(value) ? value : null;
  };

  const findNearestDraggable = (xAbs, tolerancePx = 12) => {
    const chart = chartRef.current;

    if (!chart) {
      return null;
    }

    const xScale = chart.scales?.x;

    if (!xScale) {
      return null;
    }

    const pointerPixel = xScale.getPixelForValue(xAbs);
    let best = null;

    for (const boundary of internalBoundariesAbs) {
      const boundaryPixel = xScale.getPixelForValue(boundary.x);
      const distance = Math.abs(boundaryPixel - pointerPixel);

      if (
        distance <= tolerancePx &&
        (!best || distance < best.distance)
      ) {
        best = {
          type: "wordBoundary",
          index: boundary.index,
          distance,
        };
      }
    }

    if (lastBoundaryAbs && onSetLastWordEndAbs) {
      const boundaryPixel = xScale.getPixelForValue(lastBoundaryAbs.x);
      const distance = Math.abs(boundaryPixel - pointerPixel);

      if (
        distance <= tolerancePx &&
        (!best || distance < best.distance)
      ) {
        best = {
          type: "lastEnd",
          distance,
        };
      }
    }

    if (onSetSegmentEndAbs) {
      const endPixel = xScale.getPixelForValue(segEnd);
      const distance = Math.abs(endPixel - pointerPixel);

      if (
        distance <= tolerancePx &&
        (!best || distance < best.distance)
      ) {
        best = {
          type: "segEnd",
          distance,
        };
      }
    }

    return best
      ? {
          type: best.type,
          index: best.index,
        }
      : null;
  };

  const onPointerDown = (event) => {
    const xAbs = pointerToXAbs(event);

    if (xAbs == null) {
      return;
    }

    const target = findNearestDraggable(xAbs, 12);

    // The chart surface itself no longer plays audio. Only begin a gesture
    // when the pointer is close enough to a draggable vertical boundary.
    if (!target) {
      return;
    }

    overlayRef.current?.setPointerCapture?.(event.pointerId);

    setDrag({
      ...target,
      pointerId: event.pointerId,
    });
  };

  const onPointerMove = (event) => {
    if (!drag) {
      return;
    }

    let xAbs = pointerToXAbs(event);

    if (xAbs == null) {
      return;
    }

    if (drag.type === "wordBoundary") {
      xAbs = Math.max(segStart, Math.min(segEnd, xAbs));
      onSetWordBoundaryAbs?.(drag.index, xAbs);
      return;
    }

    if (drag.type === "lastEnd") {
      xAbs = Math.max(segStart, xAbs);
      onSetLastWordEndAbs?.(xAbs);
      return;
    }

    if (drag.type === "segEnd") {
      xAbs = Math.max(segStart, xAbs);
      onSetSegmentEndAbs?.(xAbs);
    }
  };

  const onPointerUp = (event) => {
    if (!drag) {
      return;
    }

    try {
      overlayRef.current?.releasePointerCapture?.(event.pointerId);
    } catch {
      // Ignore browsers that already released capture.
    }

    setDrag(null);
  };

  useEffect(() => {
    return () => {
      setDrag(null);
    };
  }, [segment?.id]);

  if (
    !segment ||
    (!pitch?.xs?.length && !intensity?.xs?.length)
  ) {
    return null;
  }

  return (
    <div className="w-full min-w-0 bg-white rounded-xl border border-gray-200 p-2">
      <div className="text-xs text-gray-500 mb-2">
        Drag dashed boundaries • Word buttons below play exact intervals
        {" • "}
        Green/Red are segment bounds • Blue = pitch • Light gray = intensity
        {" • "}
        X axis absolute • xsMode: {xMode}
      </div>

      <div className="relative w-full h-56">
        <Line
          ref={chartRef}
          data={data}
          options={options}
        />

        <div
          ref={overlayRef}
          className="absolute inset-0"
          style={{
            touchAction: "none",
            cursor: drag ? "ew-resize" : "default",
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      </div>

      <div
        className="relative w-full h-10 mt-1"
        aria-label="Play individual words"
      >
        {wordButtonLayout.map((item) => {
          const isActive =
            cursorAbs != null &&
            cursorAbs >= item.start &&
            cursorAbs <= item.end;

          return (
            <button
              key={item.index}
              type="button"
              onClick={() =>
                onPlayRangeAbs?.(
                  item.start,
                  item.end,
                  item.index
                )
              }
              className={`absolute top-0 h-9 rounded-md border px-1 text-xs truncate transition-colors ${
                isActive
                  ? "border-blue-500 bg-blue-100 text-blue-900"
                  : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-blue-50 hover:border-blue-300"
              }`}
              style={{
                left: `${item.left}px`,
                width: `${item.width}px`,
                minWidth: "2px",
              }}
              title={`${item.text}: ${item.start.toFixed(3)}–${item.end.toFixed(3)} s`}
            >
              {item.text}
            </button>
          );
        })}
      </div>
    </div>
  );
}